/**
 * The crafting window's recipe index (Tome UI Round 4): one Tome chapter per profession, one
 * selectable row per server-sent recipe with its output's icon and a state chip. The state comes
 * from the same checks that gate the Craft button (hero level, then owned ingredients).
 */
import React from 'react';
import type { CraftingRecipe } from '../../../../shared/CharacterTypes';
import { ITEM_DATABASE } from '../../../../shared/content/generated/items.gen';
import { ItemIcon } from '../../components/ItemIcon';
import { TomeChapter, TomeChip, type TomeTone } from '../../primitives/tome';
import { craftingItemName } from './InventoryPresentation';

export type CraftingRecipeState = 'ready' | 'missing' | 'locked' | 'unknown';

/** `unknown`: the hero's level is not known yet, so the level check cannot be read. */
export function craftingRecipeState(recipe: CraftingRecipe, level: number | null, hasIngredients: boolean): CraftingRecipeState {
  if (level === null) return 'unknown';
  if (level < recipe.requiredLevel) return 'locked';
  return hasIngredients ? 'ready' : 'missing';
}

const CHIP: Record<CraftingRecipeState, { tone: TomeTone; text: (recipe: CraftingRecipe) => string }> = {
  ready: { tone: 'ready', text: () => 'Ready' },
  missing: { tone: 'available', text: () => 'Missing ingredients' },
  locked: { tone: 'locked', text: recipe => `Level ${recipe.requiredLevel}` },
  unknown: { tone: 'locked', text: () => 'Level unknown' },
};

export function professionLabel(profession: string): string {
  const words = profession.replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Other';
}

export const CraftingRecipeList: React.FC<{
  recipes: CraftingRecipe[];
  selectedId: string | undefined;
  stateOf: (recipe: CraftingRecipe) => CraftingRecipeState;
  onSelect: (recipeId: string) => void;
}> = ({ recipes, selectedId, stateOf, onSelect }) => {
  const groups = new Map<string, CraftingRecipe[]>();
  for (const recipe of recipes) {
    const label = professionLabel(recipe.profession);
    groups.set(label, [...(groups.get(label) ?? []), recipe]);
  }
  return <>{[...groups].map(([label, group]) => <TomeChapter key={label} title={label} count={group.length}>
    {group.map(recipe => {
      const state = stateOf(recipe);
      const chip = CHIP[state];
      return <button type="button" key={recipe.id} className="crafting-tome__recipe" aria-label={recipe.name}
        aria-pressed={selectedId === recipe.id} data-recipe-state={state} onClick={() => onSelect(recipe.id)}>
        <span className="crafting-tome__recipe-art" aria-hidden="true">
          {ITEM_DATABASE[recipe.outputItemId] ? <ItemIcon itemId={recipe.outputItemId} size={28} /> : '?'}
        </span>
        <span className="crafting-tome__recipe-text">
          <strong>{recipe.name}</strong>
          <span>{recipe.outputQuantity} × {craftingItemName(recipe.outputItemId)}</span>
          <TomeChip tone={chip.tone}>{chip.text(recipe)}</TomeChip>
        </span>
      </button>;
    })}
  </TomeChapter>)}</>;
};
